import React, { useEffect, useState } from "react";
import { useAdvancedValidation } from "hooks/useAdvancedValidation";
import "./ShippingDetails.css";
import MainLayout from "components/layout/main-layout/MainLayout";
import { titles } from "constants/titles";
import SubscriberInformationCard from "components/layout/subscriber-information-card/SubscriberInformationCard";
import CustomInput from "components/widgets/custom-input/CustomInput";
import { IconQuestion } from "assets/SVGs/SVGs";
import CustomButton from "components/widgets/custom-button/CustomButton";
import InformationModal from "components/layout/information-modal/InformationModal";
import CustomSelectBox from "components/widgets/custom-select-box/CustomSelectBox";
import MessageSnackBar from "components/widgets/message-snackbar/MessageSnackBar";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllRegionListNames,
  getDataFromId,
  isSessionTimeExpired,
} from "utility";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { updateMedicalAddress } from "redux/MedicalAlertUsers/actions";
import ChangesSavedModal from "components/layout/changes-saved-modal/ChangesSavedModal";
import { getRegionList } from "redux/Common/action";
import { setSessionExpiryModalVisibility } from "redux/auth/actions";

const ShippingDetails = () => {
  // Field configuration for validation (same as mobile)
  const FIELD_CONFIG = {
    firstName: {
      validation: { required: true }
    },
    lastName: {
      validation: { required: true }
    },
    street: {
      validation: { required: true }
    },
    city: {
      validation: { required: true }
    },
    state: {
      validation: { required: true }
    },
    zip_code: {
      validation: { required: true, length: 5 }
    }
  };

  const [informativePopupVisible, setInformativePopupVisible] = useState(false);
  const [shippingDetailsData, setShippingDetailsData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    street: "",
    zip_code: "",
    city: "",
    apt: "",
    state: null,
    loading: false,
  });
  const [changesSavedModal, setChangesSaveModal] = useState(false);

  const {
    errorState,
    snackbarState,
    handleFieldValidation,
    validateAllFields,
    dismissSnackbar,
    onApiResponse,
    getFieldProps,
  } = useAdvancedValidation(FIELD_CONFIG);
  const allRegionList = useSelector((store) => store?.common?.allRegionList);
  const { medicalUserDetail, loading, selectedMedicalUser } = useSelector(
    (store) => store?.medicalAlertUsers
  );
  const userData = useSelector((store) => store?.auth?.userData);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const param = {
      dispatch,
      history,
      userType: userData?.isGuestUser ? "guestuser" : "accountowner",
      emergency_contract_id: selectedMedicalUser?.service_contract_id,
      medical_alert_user_id: selectedMedicalUser?.medical_alert_user_id,
      onApiResponse,
      is_visible: true,
      is_for_mau: false
    };
    getRegionList(param);
    setInitialData();
  }, []);

  const setInitialData = () => {
    const shippingAddress =
      location?.state?.medicalUserDetail?.shipping_address;
    const regionData = getDataFromId(
      allRegionList,
      "region_id",
      shippingAddress?.region_id
    );
    handleFieldChange("firstName", shippingAddress?.firstname);
    handleFieldChange("lastName", shippingAddress?.lastname);
    handleFieldChange("apt", shippingAddress?.street2 || "");
    handleFieldChange("street", shippingAddress?.street);
    handleFieldChange("street2", shippingAddress?.street2);
    handleFieldChange("city", shippingAddress?.city);
    handleFieldChange("zip_code", shippingAddress?.zip);
    handleFieldChange(
      "state",
      regionData && regionData?.length > 0
        ? {
          id: regionData[0]?.region_id,
          value: regionData[0]?.region_code,
        }
        : null
    );
  };

  useEffect(() => {
    const shippingAddress =
      location?.state?.medicalUserDetail?.shipping_address;
    const regionData = getDataFromId(
      allRegionList,
      "region_id",
      shippingAddress?.region_id
    );
    handleFieldChange(
      "state",
      regionData && regionData?.length > 0
        ? {
          id: regionData[0]?.region_id,
          value: regionData[0]?.region_code,
        }
        : null
    );
  }, [allRegionList]);

  const handleFieldChange = (key, value) => {
    setShippingDetailsData((prevState) => ({
      ...prevState,
      [key]: value,
    }));

    // Use the advanced validation hook for field validation (same as mobile)
    handleFieldValidation(key, value);
  };

  const onSubmit = async () => {
    let timeDifference = await isSessionTimeExpired();
    const normalizedFirstName = typeof shippingDetailsData?.firstName === "string" ? shippingDetailsData?.firstName?.trim() : "";
    const normalizedLastName = typeof shippingDetailsData?.lastName === "string" ? shippingDetailsData?.lastName?.trim() : "";
    const normalizedStreet = typeof shippingDetailsData?.street === "string" ? shippingDetailsData?.street?.trim() : "";
    const normalizedCity = typeof shippingDetailsData?.city === "string" ? shippingDetailsData?.city?.trim() : "";
    const normalizedZip = typeof shippingDetailsData?.zip_code === "string" || typeof shippingDetailsData?.zip_code === "number" ? `${shippingDetailsData?.zip_code}`.trim() : "";

    // Prepare form data for validation (same as mobile)
    const formData = {
      firstName: normalizedFirstName,
      lastName: normalizedLastName,
      street: normalizedStreet,
      city: normalizedCity,
      state: shippingDetailsData?.state,
      zip_code: normalizedZip
    };

    // Validate all fields using the advanced validation hook (same as mobile)
    const isValid = validateAllFields(formData);
    if (!isValid) {
      return;
    } else if (timeDifference) {
      setSessionExpiryModalVisibility(dispatch, true);
      return;
    } else {
      const shippingAddress =
        location?.state?.medicalUserDetail?.shipping_address;
      const selectedRegion = getDataFromId(
        allRegionList,
        "region_id",
        shippingDetailsData?.state?.id
      );
      let payload = {
        address_id: shippingAddress?.address_id,
        region_id: shippingDetailsData?.state?.id,
        reference_region_id:
          selectedRegion && selectedRegion?.length > 0
            ? selectedRegion[0]?.reference_region_id
            : null,
        street: normalizedStreet,
        street2: shippingDetailsData?.apt,
        postcode: normalizedZip,
        city: normalizedCity,
        firstname: normalizedFirstName,
        lastname: normalizedLastName,
        medical_alert_user_id: medicalUserDetail?.medical_alert_user_id,
      };

      const params = {
        dispatch,
        history,
        setChangesSaveModal,
        payload,
        medical_alert_user_id: medicalUserDetail?.medical_alert_user_id,
        userType: userData?.isGuestUser ? "guestuser" : "accountowner",
        engagementType: "Update Shipping Address",
        setSnackBarMessage: onApiResponse,
      };
      updateMedicalAddress(params);
    }
  };

  const handleDropdownSelect = (value, id) => {
    handleFieldChange("state", { id: id, value: value });
  };

  return (
    <MainLayout
      text={titles?.shipping_details}
      isBackHide={false}
      isNavbar={true}
      white={true}
      isLoading={loading}
    >
      <InformationModal
        show={informativePopupVisible}
        setShow={setInformativePopupVisible}
        infoText={titles?.shipping_informative_details}
      />
      <ChangesSavedModal
        show={changesSavedModal}
        setShow={setChangesSaveModal}
      />
      {snackbarState?.show && (
        <div className="snackbar_sticky">
          <MessageSnackBar
            message={snackbarState?.message}
            isVisible={true}
            type={snackbarState?.type}
            autoHideDuration={snackbarState?.autoHideDuration}
            onDismiss={dismissSnackbar}
          />
        </div>
      )}

      <div className="sd_container">
        <SubscriberInformationCard medicalUserDetail={selectedMedicalUser} />

        <div className="sd_section sd_name_section">
          <h3 className="display_h3 left">{titles?.name}</h3>
          <CustomInput
            placeholder={titles?.first_name}
            type="text"
            value={shippingDetailsData?.firstName}
            onChange={(e) => handleFieldChange("firstName", e?.target?.value)}
            required
            error={getFieldProps("firstName")?.error}
            errorMessage={getFieldProps("firstName")?.errorMessage}
          />
          <CustomInput
            placeholder={titles?.last_name}
            type="text"
            value={shippingDetailsData?.lastName}
            onChange={(e) => handleFieldChange("lastName", e?.target?.value)}
            required
            error={getFieldProps("lastName")?.error}
            errorMessage={getFieldProps("lastName")?.errorMessage}
          />
        </div>

        {/* Protected Address Section */}
        <div className="sd_section">
          <div className="sd_section_header">
            <h3 className="display_h3">{titles?.protected_address}</h3>
            <IconQuestion
              width="24"
              height="24"
              onClick={() => setInformativePopupVisible(true)}
            />
          </div>
          <p className="body_h2 left">{titles?.shipping_address_details}</p>

          <div className="sd_address_fields">
            <CustomInput
              placeholder={titles?.street}
              type="text"
              value={shippingDetailsData?.street}
              onChange={(e) => handleFieldChange("street", e?.target?.value)}
              required
              error={getFieldProps("street")?.error}
              errorMessage={getFieldProps("street")?.errorMessage}
            />
            <CustomInput
              placeholder={titles?.apt}
              type="text"
              value={shippingDetailsData?.apt}
              onChange={(e) => handleFieldChange("apt", e?.target?.value)}
            />
            <CustomInput
              placeholder={titles?.city}
              type="text"
              value={shippingDetailsData?.city}
              onChange={(e) => handleFieldChange("city", e?.target?.value)}
              required
              error={getFieldProps("city")?.error}
              errorMessage={getFieldProps("city")?.errorMessage}
            />
            <CustomSelectBox
              required={true}
              options={getAllRegionListNames(allRegionList)}
              title={titles?.state}
              selectedValue={shippingDetailsData?.state?.value}
              onChange={(item) => handleDropdownSelect(item?.value, item?.id)}
              error={errorState?.fieldErrors?.state && errorState?.show}
            />
            <CustomInput
              placeholder={titles?.zip_code}
              type="text"
              value={shippingDetailsData?.zip_code}
              onChange={(e) => handleFieldChange("zip_code", e?.target?.value)}
              required
              error={getFieldProps("zip_code")?.error}
              errorMessage={getFieldProps("zip_code")?.errorMessage}
            />
          </div>
        </div>

        {/* Submit Button */}
        <CustomButton
          width={"100%"}
          height={"50px"}
          text={titles?.submit}
          onClick={() => onSubmit()}
        />
      </div>
    </MainLayout>
  );
};

export default ShippingDetails;
